import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Payment } from '../../../core/types';
import {
  AppBar,
  StatusChip,
  FadeInAnimation,
} from '../../../core/components/ui';

const PaymentDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const payment = (location.state as any)?.payment as
    | Payment
    | undefined;

  if (!payment) {
    return (
      <div className="page">
        <AppBar
          title="Payment Details"
          onBack={() => navigate(-1)}
        />

        <div
          style={{
            padding: 24,
            textAlign: 'center',
            color: 'var(--color-on-surface-variant)',
          }}
        >
          Payment details not found.
        </div>
      </div>
    );
  }

  const amount = Number(
    payment.payment_amount ??
      payment.bill_amount ??
      payment.amount ??
      0
  );

  const billAmount = Number(
    payment.bill_amount ??
      0
  );

  const formatDateTime = (
    dateStr?: string | null
  ) => {
    if (!dateStr) return '—';

    const date = new Date(dateStr);

    if (Number.isNaN(date.getTime())) {
      return '—';
    }

    return `${date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })} · ${date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  };

  const formatAmount = (value: number) => {
    return `${payment.currency || 'GHS'} ${value.toLocaleString(
      undefined,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    )}`;
  };

  return (
    <div
      className="page"
      style={{
        minHeight: '100vh',
        background: 'var(--color-background)',
      }}
    >
      <AppBar
        title="Payment Details"
        onBack={() => navigate(-1)}
      />

      <div
        style={{
          padding: '20px 16px 32px',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            maxWidth: 700,
            margin: '0 auto',
          }}
        >
          {/* PAYMENT SUMMARY */}
          <FadeInAnimation delay={0}>
            <div
              className="card"
              style={{
                padding: 24,
                marginBottom: 16,
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background:
                    'var(--color-primary-container)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--color-primary)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect
                    x="1"
                    y="4"
                    width="22"
                    height="16"
                    rx="2"
                  />
                  <line
                    x1="1"
                    y1="10"
                    x2="23"
                    y2="10"
                  />
                </svg>
              </div>

              <div
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  color: 'var(--color-on-surface)',
                  marginBottom: 12,
                }}
              >
                {formatAmount(amount)}
              </div>

              <StatusChip
                status={payment.status || 'PENDING'}
              />
            </div>
          </FadeInAnimation>

          {/* PAYMENT INFORMATION */}
          <FadeInAnimation delay={0.1}>
            <div
              className="card"
              style={{
                padding: 24,
                marginBottom: 16,
              }}
            >
              <SectionTitle title="Payment Information" />

              <InfoRow
                label="Payment ID"
                value={
                  payment.id ||
                  '—'
                }
              />

              <Divider />

              <InfoRow
                label="Reference Number"
                value={
                  payment.reference ||
                  '—'
                }
              />

              <Divider />

              <InfoRow
                label="Payment Amount"
                value={formatAmount(amount)}
              />

              <Divider />

              <InfoRow
                label="Payment Status"
                value={payment.status || '—'}
              />

              <Divider />

              <InfoRow
                label="Payment Date"
                value={formatDateTime(
                  payment.createdAt
                )}
              />

              <Divider />

             

              <Divider />

              <InfoRow
                label="Last Updated"
                value={formatDateTime(
                  payment.last_updated_on
                )}
              />
            </div>
          </FadeInAnimation>

         
        </div>
      </div>
    </div>
  );
};

const SectionTitle: React.FC<{
  title: string;
}> = ({ title }) => (
  <div
    style={{
      fontWeight: 700,
      fontSize: 13,
      color: 'var(--color-primary)',
      marginBottom: 16,
      textTransform: 'uppercase',
      letterSpacing: 0.8,
    }}
  >
    {title}
  </div>
);

const InfoRow: React.FC<{
  label: string;
  value: string;
}> = ({ label, value }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 20,
      padding: '4px 0',
    }}
  >
    <div
      style={{
        fontSize: 12,
        color: 'var(--color-on-surface-variant)',
        flexShrink: 0,
      }}
    >
      {label}
    </div>

    <div
      style={{
        fontSize: 13,
        fontWeight: 600,
        color: 'var(--color-on-surface)',
        textAlign: 'right',
        wordBreak: 'break-word',
      }}
    >
      {value}
    </div>
  </div>
);

const Divider: React.FC = () => (
  <div
    style={{
      height: 1,
      background: 'var(--color-outline-variant)',
      margin: '14px 0',
    }}
  />
);

export default PaymentDetailsPage;